import React, { useEffect, useState } from "react";
import { Trophy, Crown, Medal, Settings2, Plus } from "lucide-react";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { Avatar, PageCard, PillBtn, DateRange, Modal } from "./common";
import XpRulesModal from "./XpRulesModal";

const PODIUM = ["from-amber-300 to-yellow-200", "from-slate-300 to-slate-100", "from-orange-300 to-amber-100"];

export default function LeaderboardPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [range, setRange] = useState({ start: "", end: "" });
  const [data, setData] = useState(null);
  const [cat, setCat] = useState("");
  const [detail, setDetail] = useState(null);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [adjust, setAdjust] = useState(null);

  const load = async () => {
    setData(null);
    try {
      const r = await api.get("/leaderboard", { params: range });
      setData(r.data);
      setCat((c) => c || r.data.categories.find((x) => x.members.some((m) => m.user_id === user.user_id))?.role || r.data.categories[0]?.role || "");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load leaderboard"); }
  };
  useEffect(() => { load(); }, [range.start, range.end]);
  const [today, setToday] = useState(null);
  useEffect(() => {
    const d = new Date();
    const day = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    api.get("/activity/today", { params: { date: day } }).then((r) => setToday(r.data)).catch(() => {});
  }, []);

  const current = data?.categories.find((c) => c.role === cat);
  return (
    <Layout>
      <div className="p-4 lg:p-6 max-w-6xl mx-auto">
        <PageCard Icon={Trophy} title="Leaderboard" subtitle="XP is calculated automatically from tasks, meetings, events, dashboard use and recognitions." testid="leaderboard-page"
          actions={<>
            <DateRange value={range} onChange={setRange} testid="lb-range" />
            {isAdmin && <PillBtn onClick={() => setRulesOpen(true)} data-testid="lb-rules-btn"><Settings2 className="w-4 h-4" /> Points rules</PillBtn>}
          </>} />
        {!data ? <div className="text-center py-20 text-slate-400" data-testid="lb-loading">Calculating XP…</div> : (
          <>
            {data.me && <div className="bg-gradient-to-r from-sky-600 to-indigo-600 text-white rounded-2xl px-6 py-4 mb-5 flex flex-wrap items-center gap-4" data-testid="lb-my-xp">
              <Trophy className="w-8 h-8 text-amber-300" /><div><div className="text-xs uppercase tracking-wider opacity-80">Your total XP {range.start || range.end ? "(selected period)" : "— all time, up to today"}</div><div className="font-display font-black text-3xl">{data.me.xp}</div></div>
              {today && <div className="ml-auto text-right" data-testid="lb-today-active"><div className="text-xs uppercase tracking-wider opacity-80">Active today</div><div className="font-bold">{Math.min(today.active_minutes, today.required)} / {today.required} min {today.active_minutes >= today.required ? "✓ day counted" : ""}</div>
                <div className="w-40 h-1.5 bg-white/25 rounded-full mt-1 overflow-hidden"><div className="h-full bg-amber-300" style={{ width: `${Math.min(100, (today.active_minutes / today.required) * 100)}%` }} /></div></div>}
            </div>}
            <div className="flex flex-wrap gap-2 mb-5" data-testid="lb-categories">
              {data.categories.map((c) => (
                <button key={c.role} onClick={() => setCat(c.role)} data-testid={`lb-cat-${c.role}`}
                  className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${cat === c.role ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{c.label} <span className="opacity-60">({c.members.length})</span></button>
              ))}
            </div>
            {current && <Board cat={current} me={user.user_id} onOpen={setDetail} />}
          </>
        )}
      </div>
      {detail && <Modal title={detail.name} onClose={() => setDetail(null)} testid="lb-detail">
        <div className="flex items-center gap-4 mb-4"><Avatar name={detail.name} photo={detail.photo} size="w-16 h-16" /><div><div className="font-display font-black text-2xl">{detail.xp} XP</div><div className="text-sm text-slate-500">Rank #{detail.rank}</div></div></div>
        <table className="w-full text-sm"><tbody>
          {Object.entries(detail.breakdown).filter(([, n]) => n).map(([k, n]) => (
            <tr key={k} className="border-b border-slate-50"><td className="py-1.5">{data.rule_labels[k]}</td><td className="text-right text-slate-500">{n} ×</td><td className={`text-right font-bold w-20 ${data.rules[k] < 0 ? "text-rose-600" : "text-emerald-600"}`}>{data.rules[k] * n > 0 ? "+" : ""}{data.rules[k] * n}</td></tr>
          ))}
          {detail.adjustments !== 0 && <tr><td className="py-1.5">Admin adjustments</td><td /><td className="text-right font-bold">{detail.adjustments > 0 ? "+" : ""}{detail.adjustments}</td></tr>}
        </tbody></table>
        {isAdmin && <PillBtn onClick={() => { setAdjust(detail); setDetail(null); }} data-testid="lb-adjust-btn"><Plus className="w-4 h-4" /> Adjust XP</PillBtn>}
      </Modal>}
      {adjust && <AdjustModal member={adjust} onClose={() => setAdjust(null)} onDone={() => { setAdjust(null); load(); }} />}
      {rulesOpen && <XpRulesModal onClose={() => setRulesOpen(false)} onSaved={() => { setRulesOpen(false); load(); }} />}
    </Layout>
  );
}

function Board({ cat, me, onOpen }) {
  const top = cat.members.slice(0, 3);
  const rest = cat.members.slice(3);
  const order = [top[1], top[0], top[2]].filter(Boolean);
  return (
    <div data-testid={`lb-board-${cat.role}`}>
      <div className={`grid ${order.length === 1 ? "grid-cols-1 max-w-[16rem]" : order.length === 2 ? "grid-cols-2 max-w-xl" : "grid-cols-3 max-w-3xl"} gap-4 items-end mb-6 mx-auto`}>
        {order.map((m) => {
          const i = m.rank - 1;
          return (
            <button key={m.user_id} onClick={() => onOpen(m)} className={`rounded-3xl bg-gradient-to-b ${PODIUM[i]} p-4 text-center shadow-sm hover:-translate-y-1 transition-transform ${i === 0 ? "pb-10" : i === 1 ? "pb-6" : "pb-4"}`} data-testid={`lb-podium-${m.rank}`}>
              {i === 0 ? <Crown className="w-7 h-7 text-amber-600 mx-auto mb-1" /> : <Medal className="w-6 h-6 text-slate-500 mx-auto mb-1" />}
              <div className="mx-auto w-fit"><Avatar name={m.name} photo={m.photo} size={i === 0 ? "w-24 h-28 rounded-2xl" : "w-20 h-24 rounded-2xl"} ring="ring-4 ring-white" /></div>
              <div className="font-bold text-slate-900 mt-2 truncate">{m.name}</div>
              <div className="font-display font-black text-xl text-slate-800">{m.xp} XP</div>
              <div className="text-xs font-bold text-slate-600">#{m.rank}</div>
            </button>
          );
        })}
      </div>
      {rest.length > 0 && <div className="bg-white rounded-2xl border border-slate-100 divide-y divide-slate-50">
        {rest.map((m) => (
          <button key={m.user_id} onClick={() => onOpen(m)} className={`w-full flex items-center gap-4 px-5 py-3 text-left hover:bg-slate-50 ${m.user_id === me ? "bg-sky-50" : ""}`} data-testid={`lb-row-${m.user_id}`}>
            <span className="w-8 text-center font-bold text-slate-500">#{m.rank}</span>
            <Avatar name={m.name} photo={m.photo} size="w-10 h-12 rounded-lg" />
            <span className="flex-1 font-semibold truncate">{m.name}{m.user_id === me && <span className="ml-2 text-xs text-sky-600">(you)</span>}</span>
            <span className="font-display font-black text-lg">{m.xp} <span className="text-xs text-slate-400">XP</span></span>
          </button>
        ))}
      </div>}
    </div>
  );
}

function AdjustModal({ member, onClose, onDone }) {
  const [xp, setXp] = useState("");
  const [reason, setReason] = useState("");
  const save = async () => {
    try { await api.post("/leaderboard/adjust", { user_id: member.user_id, xp: parseInt(xp, 10), reason }); toast.success("XP adjusted"); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <Modal title={`Adjust XP — ${member.name}`} onClose={onClose} testid="lb-adjust-modal">
      <input type="number" value={xp} onChange={(e) => setXp(e.target.value)} placeholder="e.g. 50 or -20" className="input-pill w-full mb-2" data-testid="lb-adjust-xp" />
      <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (shown to the member)" className="input-pill w-full mb-4" data-testid="lb-adjust-reason" />
      <PillBtn primary onClick={save} data-testid="lb-adjust-save">Save</PillBtn>
    </Modal>
  );
}
